/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectorRef,
  Component,
  Injectable,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { finalize, forkJoin, fromEvent, Subject, take, takeUntil } from 'rxjs';
import moment, { Moment } from 'moment';

import {
  CalendarEvent,
  CalendarEventTitleFormatter,
  CalendarView,
  CalendarViewPeriod,
  DAYS_OF_WEEK
} from 'angular-calendar';
import { WeekViewHourSegment } from 'calendar-utils';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Client } from '../../../core/models/clients.model';
import { RRule } from 'rrule';
import { User } from '../../../core/models/api/user';
import { UserService } from '../../../core/services/user.service';
import firebase from 'firebase/compat';
import { Board } from '../../../core/models/board.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ClientService } from '../../../core/services/client.service';
import { DialogData, NewEventDialogComponent } from './new-event/new-event.component';
import { FuseLoadingService } from '../../../../@fuse/services/loading';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { getByWeekRule } from '../../../shared/utils';
import { HolidayService } from '../../../core/services/holiday.service';
import { FirstInterviewService } from '../../../core/services/first-interview.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { Event, EventCancellationTypeEnum, EventTypeEnum } from '../../../core/models/api/event';
import { EventsService } from '../../../core/providers/api/events.service';
import Timestamp = firebase.firestore.Timestamp;

export const floorToNearest = (amount: number, precision: number): any => Math.floor(amount / precision) * precision;

export const ceilToNearest = (amount: number, precision: number): any => Math.ceil(amount / precision) * precision;

@Injectable()
export class CustomEventTitleFormatter extends CalendarEventTitleFormatter {
  weekTooltip(event: CalendarEvent, title: string): any {
    if (event?.meta?.isMobile) {
      return;
    }
    return `${ event.title }`;
  }

  dayTooltip(event: CalendarEvent, title: string): any {
    if (event?.meta?.isMobile) {
      return;
    }
    return `${ event.title }`;
  }
}

moment.updateLocale('es', {
  week: {
    dow: DAYS_OF_WEEK.MONDAY,
    doy: 0
  }
});

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: [ './schedule.component.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class ScheduleComponent implements OnInit, OnDestroy {

  @ViewChild('dialogDeleteType', { static: true }) dialogDeleteType: TemplateRef<any>;
  @ViewChild('dialogEditType', { static: true }) dialogEditType: TemplateRef<any>;
  @ViewChild('dialogCancelType', { static: true }) dialogCancelType: TemplateRef<any>;

  public view: CalendarView = CalendarView.Week;
  public viewDate: Date = new Date();
  public events: any[] = [];
  public eventsToRecovery: Event[] = [];

  public dayStartHour: number = 16;

  public viewPeriod: CalendarViewPeriod;
  public showRecoveryButton: boolean = false;

  public form: UntypedFormGroup = new UntypedFormGroup({ calendarType: new UntypedFormControl(1, [ Validators.required ]) });
  public deleteForm: UntypedFormGroup = new UntypedFormGroup({ deleteType: new UntypedFormControl(1, [ Validators.required ]) });
  public editForm: UntypedFormGroup = new UntypedFormGroup({ editType: new UntypedFormControl(1, [ Validators.required ]) });

  public cancelForm = new UntypedFormGroup({});
  public model = {};
  public fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'grid grid-cols-1 gap-4',
      fieldGroup: [
        {
          key: 'cancellationType',
          type: 'select',
          className: 'col-span-1',
          props: {
            label: 'Tipo de Cancelación',
            required: true,
            options: [
              {
                id: 'reason',
                name: 'Justificada'
              },
              {
                id: 'noReason',
                name: 'NO Justificada'
              }
            ],
            valueProp: 'id',
            labelProp: 'name'
          }
        },
        {
          key: 'cancellationSubType',
          type: 'select',
          className: 'col-span-1',
          props: {
            label: 'Causa',
            required: true,
            options: [
              {
                id: 'advanceNotice',
                name: 'Aviso con anterioridad'
              },
              {
                id: 'clientIllness',
                name: 'Enfermedad del cliente'
              },
              {
                id: 'userIllness',
                name: 'Enfermedad del profesional'
              }
            ],
            valueProp: 'id',
            labelProp: 'name'
          },
          expressions: { hide: 'model.cancellationType === \'noReason\'' }
        },
        {
          key: 'cancellationReason',
          type: 'textarea',
          className: 'col-span-1',
          props: {
            label: 'Comentarios',
            required: true,
            rows: 3
          }
        }
      ]
    }
  ];

  public users: User[] = [];
  public user: User;
  public selectedUsers: string[] = [];

  public isMobile: boolean = true;
  public selectedIndex: number;

  public isFilterByFirstInterview: boolean = false;
  public clientsObject: { [id: string]: Client } = {};
  private clients: Client[] = [];

  private colors = {
    '15': {
      primary: '#c7c7c7',
      secondary: '#c7c7c7'
    },
    '30': {
      primary: '#8DBA38',
      secondary: '#AACB69'
    },
    '45': {
      primary: '#00AC8E',
      secondary: '#60AD9D'
    },
    '60': {
      primary: '#286452',
      secondary: '#5A8978'
    },
    'holiday': {
      primary: '#ff6b00',
      secondary: '#fdb988'
    },
    'internal-meeting': {
      primary: '#c7c7c7',
      secondary: '#c7c7c7'
    }
  };

  private firstInterviewBoard: Board;

  private destroy$ = new Subject<void>();

  private clientsSearched: Client[] = [];
  private totalEventsInDay: number = 16;
  private invoiceByClient: any;


  constructor(
    private cdr: ChangeDetectorRef,
    private firestore$: AngularFirestore,
    private dialog$: MatDialog,
    private user$: UserService,
    private client$: ClientService,
    private invoice$: InvoiceService,
    private firstInterview$: FirstInterviewService,
    private breakpoint$: BreakpointObserver,
    private loading$: FuseLoadingService,
    private holiday$: HolidayService,
    private event$: EventsService
  ) {
  }

  // Angular Life Cycles
  ngOnInit(): void {
    this.breakpoint$.observe('(max-width: 1023px)').subscribe(result => {
      this.isMobile = result.matches;
      if (this.isMobile) {
        this.selectedIndex = 1;
        this.changeView(1);
      }
    });

    this.user$.users$.pipe(takeUntil(this.destroy$)).subscribe(users => {
      this.users = users;
    });

    this.clientsObject = this.client$.clientsObject;
    this.clients = this.client$.clients;

    this.user$.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
      if (user.services?.length > 0) {
        this.selectedUsers.push(user?.id);
      }
    });

    let firstInterviewBoardDate = new Date().getFullYear();
    if (moment().isBefore(moment().month(9), 'month')) {
      firstInterviewBoardDate = new Date().getFullYear() - 1;
    }
    this.firstInterview$.getBoard(firstInterviewBoardDate).subscribe(board => {
      this.firstInterviewBoard = board;
    });

    this.getInvoices();
  }

  getInvoices(): void {
    forkJoin([
      this.invoice$.getInvoiceByMonth(moment(this.viewDate).subtract(1, 'month')).pipe(take(1)),
      this.invoice$.getInvoiceByMonth(moment(this.viewDate)).pipe(take(1)),
      this.invoice$.getInvoiceByMonth(moment(this.viewDate).add(1, 'month')).pipe(take(1))
    ])
      .pipe(take(1))
      .subscribe(invoicesPack => {
        this.invoiceByClient = {};
        invoicesPack.forEach(pack => {
          pack.forEach(invoice => {
            if (!this.invoiceByClient[moment((invoice.invoiceDate as Timestamp).toDate()).month()]) {
              this.invoiceByClient[moment((invoice.invoiceDate as Timestamp).toDate()).month()] = {};
            }
            this.invoiceByClient[moment((invoice.invoiceDate as Timestamp).toDate()).month()][invoice.clientId] = invoice;

          });
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Events of Angular-Calendar
  updateCalendarEvents(viewRender: any, reload: boolean = false): void {
    if (
      !this.viewPeriod ||
      !moment(this.viewPeriod.start).isSame(viewRender.period.start) ||
      !moment(this.viewPeriod.end).isSame(viewRender.period.end) || reload
    ) {
      this.viewPeriod = viewRender.period;
      if (this.clientsSearched.length > 0) {
        this.onSubmit(this.clientsSearched);
      } else {
        this.getEvents();
      }
    }
  }

  viewDateChange($event: Date): void {
    this.viewDate = $event;
  }

  refresh(): void {
    this.events = [ ...this.events ];
    this.cdr.detectChanges();
    this.loading$.hide();
  }

  startDragToCreate(segment: WeekViewHourSegment, mouseDownEvent: MouseEvent, segmentElement: HTMLElement): void {

    const dragToSelectEvent: CalendarEvent = {
      title: 'Nuevo Horario: ' + moment(segment.date).format('HH:mm'),
      start: segment.date
    };
    this.events = [ ...this.events, dragToSelectEvent ];
    const segmentPosition = segmentElement.getBoundingClientRect();
    const endOfView = moment(this.viewPeriod.end).endOf('week');
    fromEvent(document, 'mousemove')
      .pipe(
        finalize(() => {
          delete dragToSelectEvent.title;
          const diag = this.dialog$.open(NewEventDialogComponent, {
            width: '800px',
            minWidth: this.isMobile ? '100vw' : 'auto',
            height: this.isMobile ? '100vh' : 'auto',
            panelClass: 'rounded-none',
            autoFocus: false,
            data: {
              usersId: this.selectedUsers.length > 0 ?
                this.users.filter(f => this.selectedUsers.indexOf(f.id) !== -1).map(m => m.id) :
                [ this.user.id ],
              ...dragToSelectEvent
            }
          });
          diag.afterClosed().subscribe((closeEvent: { data: DialogData; eventType: 'exit' | 'submit' }) => {
            if (!closeEvent || closeEvent?.eventType === 'exit') {
              this.events.pop();
              this.refresh();
            } else {
              this.loading$.show();
              this.createEvent(closeEvent.data);
            }
          });
        }),
        takeUntil(fromEvent(document, 'mouseup'))
      )
      .subscribe((mouseMoveEvent: MouseEvent) => {
        const minutesDiff = ceilToNearest(
          mouseMoveEvent.clientY - segmentPosition.top,
          15
        );

        const daysDiff =
          floorToNearest(
            mouseMoveEvent.clientX - segmentPosition.left,
            segmentPosition.width
          ) / segmentPosition.width;

        const newEnd = moment(segment.date).add(minutesDiff, 'minutes').add(daysDiff, 'days');
        if (newEnd > moment(segment.date) && newEnd < endOfView && moment(dragToSelectEvent.start).isSame(newEnd, 'day')) {
          dragToSelectEvent.end = newEnd.toDate();
        }
        dragToSelectEvent.title = 'Nuevo evento: ' +
          moment(dragToSelectEvent.start).format('HH:mm') + ' - ' + moment(dragToSelectEvent.end).format('HH:mm');

        this.refresh();
      });
  }

  handleEvent(action: string, event: any): void {
    let dialogTemplate;
    if (action === 'delete') {
      dialogTemplate = this.dialogDeleteType;
    } else if (action === 'edit') {
      dialogTemplate = this.dialogEditType;
    } else if (action === 'cancel') {
      dialogTemplate = this.dialogCancelType;
    } else if (action === 'restore') {
      this.restoreEvent(event);
    } else if (action === 'recovery') {
      this.recoveryEvent(event);
    } else if (action === 'no-recovery') {
      this.setNoRecovery(event);
    }

    if (dialogTemplate) {
      const diag = this.dialog$.open(dialogTemplate, {
        width: '400px',
        height: (action === 'delete' || action === 'edit') ? '300px' : '400px',
        disableClose: false,
        autoFocus: false,
        data: { event }
      });

      diag.afterClosed().subscribe((result) => {
        if (result) {
          this.loading$.show();
          if (action === 'delete') {
            this.deleteEvent(event, this.deleteForm.controls.deleteType.value);
          } else if (action === 'edit') {
            this.editEvent(event, this.editForm.controls.editType.value);
          } else if (action === 'cancel') {
            this.cancelEvent(event);
          }
        }
      });
    }
  }

  changeView(index: number): void {
    this.selectedIndex = index;

    switch (index) {
      case 0:
        this.view = CalendarView.Week;
        break;
      case 1:
        this.view = CalendarView.Day;
        break;
    }
  }

  changeUser(user: User): void {
    const index = this.selectedUsers.indexOf(user?.id);
    if (index !== -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user?.id);
    }
    this.getEvents();
  }

  filterByFirstInterview(): void {
    this.isFilterByFirstInterview = !this.isFilterByFirstInterview;
    this.getEvents();
  }

  transformToCalendar(event: Event, empty: boolean = false): any {
    let isCancelled = false;
    if (event.cancellationType === EventCancellationTypeEnum.reason
      || event.cancellationType === EventCancellationTypeEnum.noReason
    ) {
      isCancelled = true;
    }

    if (event.clientsId?.length === 1) {
      event.extraInfo = {
        invoice: this.invoiceByClient && Object.keys(this.invoiceByClient).length > 0 && this.invoiceByClient[moment(event.start).month()]
          ? this.invoiceByClient[moment(event.start).month()][event.clientsId[0]]
          : null
      };
    }

    return event.usersId.filter(f =>
      this.selectedUsers.length > 0
        ? this.selectedUsers.indexOf(f) !== -1
        : true
    ).sort((a, b) => {
      if (a > b) {
        return 1;
      } else if (b > a) {
        return -1;
      }
      return 0;
    }).map(id => ({
      id: event.id,
      start: event.start,
      end: event.end,
      color: this.getColor(event, id, empty),
      title: this.getTitleOfSession(event),
      meta: {
        isMobile: this.isMobile,
        event: {
          ...event,
          start: event.start,
          end: event.end
        }
      },
      cssClass: 'text-white' + (isCancelled ? ' bg-cancelled' : (event.extraInfo && !event.extraInfo['invoice']?.paidDate ? ' bg-not-paid' : ''))
    }));
  }

  holidayToCalendar(event: any): any {
    return {
      id: null,
      start: event.date,
      end: moment(event.date).endOf('day').toDate(),
      color: this.colors['holiday'],
      title: event.name,
      meta: { event },
      cssClass: 'text-white'
    };
  }

  getTitleOfSession(event): string {
    let title = '';
    switch (event.type) {
      case EventTypeEnum.session:
        let client;
        if (event.clientsId?.length > 0) {
          client = this.clients.find(f => event.clientsId?.indexOf(f.id) !== -1);
        }
        const date = event.start;
        const date2 = event.end;

        title = client ? client?.name + ' ' + client?.lastname1 + '<br />'
          + moment(date).format('HH:mm') + ' - ' + moment(date2).format('HH:mm') : '';
        break;
      case  EventTypeEnum.firstInterview:
        if (event.boardCard) {
          const [ board, card ] = event.boardCard.split('-');
          title += '<p>1ª Entrevista <br />';

          if (board) {
            this.firstInterviewBoard?.lists.forEach(list => {
              const c = list.cards.find(crd => crd.id === card);
              if (c) {
                title += c.title;
              }
            });
          }
        }
        break;
      case EventTypeEnum.other:
      case EventTypeEnum.internalMeeting:
        title = event.title;
        break;
    }

    return title;
  }

  getColor(event, userId, empty: boolean): { primary: string; secondary: string } {
    const color = this.users.find(u => u.id === userId)?.color;
    if (this.selectedUsers.length === 1 && event.type === EventTypeEnum.internalMeeting) {
      return this.colors['internal-meeting'];
    }

    return this.selectedUsers.length === 1
      ? this.colors[Math.round(moment(event.end).diff(moment(event.start), 'minute', true))]
      : {
        // primary: color,
        primary: empty ? 'rgba(241, 245, 249, 0.12)' : color,
        secondary: empty ? 'rgba(241, 245, 249, 0.12)' : color
      };
  }

  filterSelectedUsers(event: any): boolean {
    return this.selectedUsers.length === 0 ||
      (this.selectedUsers?.length > 0 && event.usersId.filter(f => this.selectedUsers.indexOf(f) !== -1).length > 0);
  }

  // Functions relative to Events
  async getEvents(clientsToFilter?: Client[]): Promise<void> {
    this.loading$.show();
    await this.getEventsPendingOfRestore(clientsToFilter);

    // this.firestore$.collection<Event>('events',
    //   q => q
    //     .where('start', '>', moment(this.viewDate).startOf('week').toDate())
    //     .where('start', '<', moment(this.viewDate).endOf('week').toDate())
    // )
    //   .valueChanges({ idField: 'id' })
    //   .pipe(take(1))
    this.event$.eventsControllerFindWithFilter({
      start: moment(this.viewDate).startOf('week').toDate(),
      end: moment(this.viewDate).endOf('week').toDate()
    })
      .subscribe(events => {
        this.events = events
          .filter(
            event => this.isFilterByFirstInterview ? event.type === EventTypeEnum.firstInterview : true
          )
          .filter(
            event => this.filterSelectedUsers(event)
          )
          .filter(
            event => clientsToFilter?.length > 0
              ? (event.clientsId?.length > 0 && clientsToFilter.map(m => m.id).some(s => s === event.clientsId[0]))
              : true
          )
          .map(m => this.transformToCalendar(m)).flat();


        for (let i = 1; i < 6; i++) {
          (this.selectedUsers.length > 0 ? this.selectedUsers.map(m => ({ id: m })) : this.users.filter(f => f.services?.length > 0)).forEach(user => {
            const evs = this.getEventsByUserByDay(
              this.events,
              user.id,
              moment(this.viewDate).day(i)
            ).map(m => this.transformToCalendar(m, true)).flat();
            this.events = [
              ...this.events,
              ...(evs.length === this.totalEventsInDay ? [] : evs)
            ];
          });
        }

        this.events = [
          ...this.events.sort((a, b) => {
            if (a.meta.event.usersId[0] > b.meta.event.usersId[0]) {
              return 1;
            } else if (b.meta.event.usersId[0] > a.meta.event.usersId[0]) {
              return -1;
            }
            return 0;
          })
        ];

        this.holiday$.holidays.forEach(m => this.events.push(this.holidayToCalendar(m)));
        this.loading$.hide();
        this.refresh();
      });
  }

  getEventsByUserByDay(events: any[], user: string, day: Moment): Event[] {

    const evs = events.filter(f =>
      f?.meta?.event?.usersId?.includes(user) &&
      moment(f.start as Date).isBetween(moment(day).startOf('day'), moment(day).endOf('day'))
    ).sort((a, b) => {
      if (moment(a.start).isBefore(b.start)) {
        return -1;
      } else if (moment(b.start).isBefore(a.start)) {
        return 1;
      }
      return 0;
    }).map(m => ({
      start: moment(m.start).format('HH:mm'),
      end: moment(m.end).format('HH:mm')
    }));

    return this.createAvailableEvents(this.dayStartHour < 16 ? '10:00' : '16:00', '20:00', evs).map(m => ({
      start: moment(day.format('YYYY-MM-DD') + ' ' + m, 'YYYY-MM-DD HH:mm').toDate(),
      end: moment(day.format('YYYY-MM-DD') + ' ' + m, 'YYYY-MM-DD HH:mm').add(15, 'minutes').toDate(),
      usersId: [ user ],
      type: EventTypeEnum.empty,
      id: 'temp'
    }));
  }

  getEventsPendingOfRestore(clientsToFilter?: Client[]): Promise<void> {
    return new Promise((resolve) => {
      // this.firestore$.collection<Event>('events',
      //   q => q
      //     .where('start', '>=', moment(this.viewDate).startOf('month').toDate())
      //     .where('start', '<=', moment(this.viewDate).endOf('month').toDate())
      //     .where('isRecovered', '==', false)
      //     .where('isPaid', '==', true)
      // )
      //   .valueChanges({ idField: 'id' })
      //   .pipe(take(1))
      this.event$.eventsControllerFindWithFilter({
        start: moment(this.viewDate).startOf('month').toDate(),
        end: moment(this.viewDate).endOf('month').toDate(),
        isRecovered: false,
        isPaid: true
      })
        .subscribe(events => {
          this.eventsToRecovery = events
            .filter(
              event => (event.clientsId?.length > 0 && clientsToFilter?.length > 0)
                ? (clientsToFilter.map(m => m.id).some(s => s === event.clientsId[0]))
                : true
            ).filter(f => !!f.cancellationType && f.cancellationType === EventCancellationTypeEnum.reason);
          resolve();
        });
    });
  }

  showDetail(event: Event): void {
    if (event.type === EventTypeEnum.empty && !this.user.isAdmin) {
      return;
    } else if (event.type === EventTypeEnum.empty && this.user.isAdmin) {
      const diag = this.dialog$.open(NewEventDialogComponent, {
        width: '800px',
        minWidth: this.isMobile ? '100vw' : 'auto',
        height: this.isMobile ? '100vh' : 'auto',
        panelClass: 'rounded-none',
        autoFocus: false,
        data: {
          usersId: this.selectedUsers.length > 0 ?
            this.users.filter(f => this.selectedUsers.indexOf(f.id) !== -1).map(m => m.id) :
            [ this.user.id ],
          ...{
            start: event.start,
            end: event.end,
            title: 'test'
          }
        }
      });
      diag.afterClosed().subscribe((closeEvent: { data: DialogData; eventType: 'exit' | 'submit' }) => {
        if (!closeEvent || closeEvent?.eventType === 'exit') {
          this.events.pop();
          this.refresh();
        } else {
          this.loading$.show();
          this.createEvent(closeEvent.data);
        }
      });
      return;
    } else {

    }


    const dialog = this.dialog$.open(NewEventDialogComponent, {
      width: '800px',
      minWidth: this.isMobile ? '100vw' : 'auto',
      height: this.isMobile ? '100vh' : 'auto',
      panelClass: 'rounded-none',
      autoFocus: false,
      data: { ...event }
    });

    dialog.afterClosed().subscribe((closeEvent: {
      data: DialogData;
      eventType: 'exit' | 'submit' | 'edit' | 'delete';
    }) => {
      if (!closeEvent || closeEvent.eventType === 'exit') {
        this.events.pop();
      } else {
        this.handleEvent(closeEvent.eventType, closeEvent.data);
      }
    });
  }

  createEvent(event: DialogData): void {
    const ruleOptions: any = {
      dtstart: event.start,
      freq: RRule.WEEKLY,
      byweekday: getByWeekRule(event.daySelected),
      interval: event.periodicity,
      until: event.calendarType === 1
        ? (moment(event.start).isBefore(moment().month(6)) ? moment() : moment().add(1, 'y'))
          .month(6)
          .endOf('month')
          .startOf('week')
          .day(+event.daySelected.slice(-1)[0])
          .toDate()
        : event.end
    };

    const rrule = new RRule(ruleOptions);
    const timeAmount = moment(event.end).diff(moment(event.start), 'hour', true);

    let hours;
    const parentId = this.firestore$.createId();

    delete event.calendarType;
    delete event.daySelected;

    const forkJoinArr: Promise<void>[] = [];

    rrule.all().forEach(ev => {
      const id = this.firestore$.createId();

      if (!hours) {
        hours = ev.getHours();
      }
      ev.setHours(hours);
      forkJoinArr.push(this.firestore$.collection<Event>('events').doc(id).set({
        ...event,
        id,
        parentId,
        start: moment(ev).toDate(),
        end: moment(ev).add(timeAmount, 'hour').toDate(),
        cancellationType: null,
        cancellationSubType: null,
        cancellationReason: null,
        comments: [],
        isPaid: true,
        isRecovery: null,
        isRecovered: null,
        paidDate: null
      }));
    });

    Promise.all(forkJoinArr).then(() => {
      this.getEvents();
    });
  }

  editEvent(event: Event, editType: number): void {
    const forkJoinArr: Promise<any>[] = [];
    let observable;
    if (editType === 1) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref
          .where('parentId', '==', event.parentId)
      ).get();
    } else if (editType === 2) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref
          .where('parentId', '==', event.parentId)
          .where('start', '>', moment(event.start).startOf('day').toDate())
      ).get();
    } else if (editType === 3) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref.where('id', '==', event.id)
      ).get();
    }

    if (observable) {
      observable.subscribe(result => {
        result.docs.forEach(f => {
          const e = f.data();

          if (editType !== 3) {
            event.start = moment((e.start as Timestamp).toDate())
              .hours(moment(event.start).hours())
              .minutes(moment(event.start).minutes()).toDate();

            event.end = moment((e.end as Timestamp).toDate())
              .hours(moment(event.end).hours())
              .minutes(moment(event.end).minutes()).toDate();
          } else {
            event.parentId = this.firestore$.createId();
          }

          event.id = e.id;
          event.cancellationType = e.cancellationType || null;
          event.cancellationSubType = e.cancellationSubType || null;
          event.cancellationReason = e.cancellationReason || null;
          event.comments = e.comments || null;
          event.isPaid = e.isPaid || null;
          event.isRecovery = e.isRecovery || null;
          event.isRecovered = e.isRecovered || null;
          event.paidDate = e.paidDate || null;

          delete event['extraInfo'];
          forkJoinArr.push(f.ref.update(event));
        });

        Promise.all(forkJoinArr).then(() => {
          this.getEvents();
        });
      });
    }
  }

  deleteEvent(event: Event, deleteType: number): void {
    let observable;
    if (deleteType === 1) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref.where('parentId', '==', event.parentId)).get();
    } else if (deleteType === 2) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref
          .where('parentId', '==', event.parentId)
          .where('start', '>', moment(event.start).startOf('day').toDate())
      ).get();

    } else if (deleteType === 3) {
      observable = this.firestore$.collection<Event>('events', ref =>
        ref.where('id', '==', event.id)
      ).get();
    }

    if (observable) {
      observable
        .subscribe(result => {
          const promiseArr: Promise<void>[] = [];
          result.docs.forEach(f => {
            promiseArr.push(f.ref.delete());
          });
          Promise.all(promiseArr).then(() => this.getEvents());
        });
    }
  }

  cancelEvent(event: Event): void {
    const { cancellationType, cancellationSubType = null, cancellationReason } = this.cancelForm.value;
    let isRecovered: boolean;
    if (cancellationType === EventCancellationTypeEnum.reason) {
      if (event.isPaid !== null && event.isPaid !== undefined) {
        isRecovered = !event.isPaid;
      } else {
        isRecovered = false;
      }
    } else if (cancellationType === EventCancellationTypeEnum.noReason) {
      isRecovered = true;
    }

    this.firestore$.collection<Event>('events').doc(event.id).update({
      isRecovered,
      cancellationType,
      cancellationSubType,
      cancellationReason
    }).then(() => {
      this.getEvents();
    });
  }

  restoreEvent(event: Event): void {
    event.isRecovered = null;
    event.cancellationType = null;
    event.cancellationSubType = null;
    event.cancellationReason = null;

    this.firestore$.collection<Event>('events').doc(event.id).update({ ...event }).then(() => {
      this.getEvents();
    });
  }

  setNoRecovery(event: Event): void {
    event.isRecovered = true;

    this.firestore$.collection<Event>('events').doc(event.id).update({ ...event }).then(() => {
      this.getEvents();
    });
  }

  async recoveryEvent(event: Event): Promise<void> {

    await this.firestore$.collection<Event>('events').doc(event.id).update({ isRecovered: true });

    const id = this.firestore$.createId();
    const parentId = this.firestore$.createId();

    await this.firestore$.collection<Event>('events').doc(id).set({
      ...event,
      isRecovered: false,
      isRecovery: true,
      cancellationType: null,
      cancellationSubType: null,
      cancellationReason: null,
      comments: [],
      id,
      parentId
    });
    this.getEvents();
  }

  onSubmit(clients): void {
    this.clientsSearched = clients;
    this.getEvents(clients);
  }

  showMorningSchedule(): void {
    this.dayStartHour = this.dayStartHour < 16 ? 16 : 10;
    this.totalEventsInDay = this.dayStartHour < 16 ? 36 : 16;
    this.getEvents();
  }

  createAvailableEvents(startTime: string, endTime: string, busyEvents: { start: string; end: string }[]): string[] {

    const start = moment(`1970-01-01T${ startTime }`);
    // const start = new Date(`1970-01-01T${ startTime }`);
    const end = moment(`1970-01-01T${ endTime }`);
    // const end = new Date(`1970-01-01T${ endTime }`);
    const interval = 15; // Intervalo de 30 minutos en minutos

    let current = moment(start);
    const availableEvents: string[] = [];

    for (const busyEvent of busyEvents) {
      const busyStart = moment(`1970-01-01T${ busyEvent.start }`);
      const busyEnd = moment(`1970-01-01T${ busyEvent.end }`);

      while (current.isBefore(busyStart)) {
        const currentTimeStr = current.format('HH:mm');
        availableEvents.push(currentTimeStr);
        current.minutes(current.minutes() + interval);
      }

      if (busyEnd.isAfter(current)) {
        current = busyEnd;
      }
    }

    while (current.isBefore(end)) {
      const currentTimeStr = current.format('HH:mm');
      availableEvents.push(currentTimeStr);
      current.minutes(current.minutes() + interval);
    }

    return availableEvents;
  }

}
